import { WelcomeVideo } from '../../domain/entities/WelcomeVideo';
import { WelcomeVideoDTO, CreateWelcomeVideoInput } from '../dto/WelcomeVideoDTO';
import { WelcomeVideoType } from '../../domain/value-objects/WelcomeVideoType';
import { TargetRole } from '../../domain/value-objects/TargetRole';

/**
 * WelcomeVideo Mapper
 * Converts between WelcomeVideo entities and DTOs
 */
export class WelcomeVideoMapper {
  static toDTO(video: WelcomeVideo): WelcomeVideoDTO {
    return {
      id: video.id,
      workspaceId: video.workspaceId,
      cycleId: video.cycleId,
      cycleName: video.cycleName,
      targetRole: video.targetRole,
      videoType: video.videoType,
      videoUrl: video.videoUrl,
      title: video.title,
      description: video.description,
      thumbnailUrl: video.thumbnailUrl,
      displayOrder: video.displayOrder,
      isActive: video.isActive,
      showOnce: video.showOnce,
      scheduledAt: video.scheduledAt?.toISOString() ?? null,
      expiresAt: video.expiresAt?.toISOString() ?? null,
      createdBy: video.createdBy,
      createdAt: video.createdAt.toISOString(),
      updatedAt: video.updatedAt.toISOString(),
      isViewed: video.isViewed,
      viewedAt: video.viewedAt?.toISOString() ?? null,
      completed: video.completed,
    };
  }

  static toDomain(dto: WelcomeVideoDTO): WelcomeVideo {
    return new WelcomeVideo({
      id: dto.id,
      workspaceId: dto.workspaceId,
      cycleId: dto.cycleId,
      cycleName: dto.cycleName,
      targetRole: dto.targetRole,
      videoType: dto.videoType,
      videoUrl: dto.videoUrl,
      title: dto.title,
      description: dto.description,
      thumbnailUrl: dto.thumbnailUrl,
      displayOrder: dto.displayOrder,
      isActive: dto.isActive,
      showOnce: dto.showOnce,
      scheduledAt: dto.scheduledAt ? new Date(dto.scheduledAt) : null,
      expiresAt: dto.expiresAt ? new Date(dto.expiresAt) : null,
      createdBy: dto.createdBy,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
      isViewed: dto.isViewed,
      viewedAt: dto.viewedAt ? new Date(dto.viewedAt) : null,
      completed: dto.completed,
    });
  }

  static toDTOList(videos: WelcomeVideo[]): WelcomeVideoDTO[] {
    return videos.map((video) => this.toDTO(video));
  }

  static toDomainList(dtos: WelcomeVideoDTO[]): WelcomeVideo[] {
    return dtos.map((dto) => this.toDomain(dto));
  }

  /**
   * Map raw database row to domain entity
   */
  static fromDatabase(row: Record<string, unknown>): WelcomeVideo {
    return new WelcomeVideo({
      id: row.id as number,
      workspaceId: row.workspace_id as number,
      cycleId: row.cycle_id as number | null,
      cycleName: row.cycle_name as string | null,
      targetRole: row.target_role as TargetRole,
      videoType: row.video_type as WelcomeVideoType,
      videoUrl: row.video_url as string,
      title: row.title as string,
      description: row.description as string | null,
      thumbnailUrl: row.thumbnail_url as string | null,
      displayOrder: (row.display_order as number) ?? 0,
      isActive: (row.is_active as boolean) ?? true,
      showOnce: (row.show_once as boolean) ?? true,
      scheduledAt: row.scheduled_at ? new Date(row.scheduled_at as string) : null,
      expiresAt: row.expires_at ? new Date(row.expires_at as string) : null,
      createdBy: row.created_by as string | null,
      createdAt: new Date(row.created_at as string),
      updatedAt: new Date(row.updated_at as string),
      isViewed: row.is_viewed as boolean | undefined,
      viewedAt: row.viewed_at ? new Date(row.viewed_at as string) : null,
      completed: row.completed as boolean | undefined,
    });
  }

  /**
   * Map create input to database format
   */
  static toCreatePayload(input: CreateWelcomeVideoInput): Record<string, unknown> {
    return {
      cycle_id: input.cycleId ?? null,
      target_role: input.targetRole,
      video_type: input.videoType,
      video_url: input.videoUrl,
      title: input.title,
      description: input.description ?? null,
      thumbnail_url: input.thumbnailUrl ?? null,
      display_order: input.displayOrder ?? 0,
      is_active: input.isActive ?? true,
      show_once: input.showOnce ?? true,
      scheduled_at: input.scheduledAt ?? null,
      expires_at: input.expiresAt ?? null,
    };
  }
}
